import React from "react";
import { toast } from "sonner";

import { usePatchAgent } from "@/shared/api/agents";
import { Group } from "@/shared/components/settings-atoms";
import { EditorHeader } from "./EditorHeader";
import { LimitRow } from "./LimitRow";
import { WhoLands } from "./WhoLands";
import { DEFAULT_DAILY_BUDGET, DEFAULT_MESSAGE_LIMIT } from "@/shared/types";

import type { AgentRow } from "@/shared/types";
import type { RuleMatches } from "../../types";

/* Everyone else: anyone no policy matches. Its limits are the agent's own (`PATCH /agents`),
   and the ones an allow policy without its own falls back to: such a policy spends from
   Everyone else's budget. It is checked last and can't be moved, switched off or deleted. */
export function EveryoneElseEditor({ agent, matches }: { agent: AgentRow; matches: RuleMatches }) {
  const patch = usePatchAgent();
  const shared = agent.visibility !== "private";
  const save = (body: { daily_budget_usd: number | null } | { message_limit: number | null }, said: string) =>
    patch.mutate({ id: agent.id, body }, { onSuccess: () => toast.success(said) });

  return (
    <div aria-label="Everyone else" className="flex flex-col">
      <EditorHeader n={null} kicker="Checked last · can't be moved or deleted">
        <div className="text-[14px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Everyone else</div>
      </EditorHeader>
      <Group title="Applies to" desc="Anyone the policies above don't match." />
      <Group title="Then" desc={`${agent.name} lets them in, with these limits.`}>
        <div className="rounded-[12px] ring-1 ring-[var(--hairline)] px-3 divide-y divide-[var(--hairline)]">
          <LimitRow
            field="daily_budget_usd"
            label="Daily budget (USD)"
            placeholder={shared ? String(DEFAULT_DAILY_BUDGET) : "None"}
            hint={`What ${agent.name} may spend a day (UTC) on everyone without a budget of their own above. ${shared ? `Empty means the default, $${DEFAULT_DAILY_BUDGET}.` : "Empty means no limit."}`}
            value={agent.daily_budget_usd}
            onSave={(v) => save({ daily_budget_usd: v }, v === null ? "Daily budget back to the default" : `Daily budget is $${v} a day`)}
          />
          <LimitRow
            field="message_limit"
            label="Messages per person, per 10 minutes"
            placeholder={String(DEFAULT_MESSAGE_LIMIT)}
            hint={`Counted across every interface together. Empty means the default, ${DEFAULT_MESSAGE_LIMIT}.`}
            value={agent.message_limit}
            onSave={(v) => save({ message_limit: v }, v === null ? "Messages per person back to the default" : `${v} messages per person every ten minutes`)}
          />
        </div>
      </Group>
      <WhoLands matches={matches} off={false} agentName={agent.name} shadow={null} />
    </div>
  );
}
